import isEqual from 'underscore/modules/isEqual';
import { featureCollection, point, lineString } from '@turf/helpers';
import { addPolylines, removePolylines } from './polylines';
import { addChevrons, removeChevrons } from './chevrons';
import { addMarkers, removeMarkers } from './markers';
import { addLayer, addLayerEvent } from '../layers/layer_helpers';
import { createLabelLayer } from '../layers/labels';
import { closestInterpPt } from '../../trail_planner/closest_on_line';
import { addOrUpdateGeojsonSource } from '../sources/geojson_helpers';
import { getDraggableId } from './draggable';
import { isCloseToEnds } from '../point_helpers';
import { END, NODE, PLUS, START } from 'utils/constants/MarkerIds';

const trailPlannerId = 'trailPlannerMap';
const routeNodesId = `${trailPlannerId}-nodes`;
const routeNodesDragId = getDraggableId(routeNodesId);
const routePolylinesId = `${trailPlannerId}-polylines`;
const routeInsertModePolylinesId = `${trailPlannerId}-insert-mode-polylines`;
const routeChevronsId = `${trailPlannerId}-chevrons`;
const routeInsertModeChevronsId = `${trailPlannerId}-insert-route-chevrons`;
const newRouteNodeId = 999999999;

const styleRouteNodes = (trailPlanner, messages, hasAdminFeatures = false) => {
  const nodes = trailPlanner.routeNodes;

  return nodes.map((routeNode, i) => {
    const iconImage = getRouteNodeMarkerType(nodes, routeNode, i);
    const labelMessage = iconImage === 'map-marker-node' && messages.dragToChange;
    const positionMessage = getRouteNodePositionMessage(routeNode, messages, hasAdminFeatures);

    return {
      ...routeNode,
      iconImage,
      labelMessage,
      positionMessage 
    };
  });
};

const getRouteNodePositionMessage = (routeNode, messages, hasAdminFeatures) => {
  if (!hasAdminFeatures || !routeNode?.position){
    return undefined;
  }

  return messages[`${routeNode.position}Selected`];
}

const getRouteNodeMarkerType = (nodes, node, i) => {
  if (i === 0) {
    return 'map-marker-start';
  }
  if (i === nodes.length - 1) {
    if (isEqual(node.lngLat, nodes[0].lngLat)) {
      return 'map-marker-start-end';
    } else {
      return 'map-marker-end';
    }
  }
  return 'map-marker-node';
};

const addPolylineEvents = (map, trailPlanner, layerId, pointText) => {
  // Remove new route node on mouse-out
  const onMouseLeave = () => {
    map.getCanvas().style.cursor = map.customStyle.cursor;
    addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
  };

  // Show new route node when mousing-over polylineGeojson
  // Note: Here we are reusing the routeNodesDragId source/layer for this node to reduce the need to add yet another layer
  const onMouseMove = e => {
    if (map.shiftKeyDown) {
      onMouseLeave();
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
    const polylineIdx = e.features[0].properties.idx;
    const polyline = trailPlanner.routePolylines[polylineIdx];
    const points = polyline.points;
    if (isCloseToEnds(map, e.lngLat, points, 16)) {
      addOrUpdateGeojsonSource(map, routeNodesDragId, featureCollection([]));
      return;
    }
    const closest = closestInterpPt(e.lngLat.toArray(), points);
    const closestLngLat = closest.interpolatedPoint ? closest.interpolatedPoint : points[closest.cropIndex];
    const newRouteNode = {
      id: newRouteNodeId,
      cropIdx: closest.cropIndex,
      polylineIdx,
      iconImage: 'map-marker-plus',
      addPointText: pointText
    };
    const newRouteNodeGeojson = point(closestLngLat, newRouteNode, { id: newRouteNode.id });
    addOrUpdateGeojsonSource(map, routeNodesDragId, newRouteNodeGeojson);
    if (!map.dragging) {
      addLayer(map, createLabelLayer(`${routeNodesDragId}-labels`, routeNodesDragId, 'addPointText', true));
    }
  };

  addLayerEvent(map, layerId, true, 'mousemove', onMouseMove, false);
  addLayerEvent(map, layerId, true, 'mouseleave', onMouseLeave, false);
};

const addNodeEvents = (map, trailPlanner) => {
  // Handle route node dragged to new location
  addLayerEvent(map, routeNodesId, true, 'draglayerend', e => {
    const routeNode = e.draggingFeature.properties;
    trailPlanner.routeNodeMoved(routeNode, e.lngLat.toArray());
  });
};

const addTrailPlannerOverlay = (map, trailPlanner, lineColor, messages, hasAdminFeatures = false) => {
  // Draw route polylines
  const layerId = trailPlanner.isInsertTrailPlanner ? routeInsertModePolylinesId : routePolylinesId;
  const chevronsId = trailPlanner.isInsertTrailPlanner ? routeInsertModeChevronsId : routeChevronsId; 
  const color = trailPlanner.color;
  const lineStrings = trailPlanner.routePolylines.map((routePolyline, idx) => lineString(routePolyline.points, { idx, color }));
  const polylineGeojson = featureCollection(lineStrings);
  const polylineLayerExists = map.getLayer(layerId);
  const polylineOpts = lineColor ? { 'line-color': lineColor } : null;
  addPolylines(map, layerId, polylineGeojson, {} /* sourceOpts */, polylineOpts);
  addChevrons(map, chevronsId, layerId);
  if (!polylineLayerExists) {
    addPolylineEvents(map, trailPlanner, layerId, messages.clickToAddPoint);
  }

  // Draw route nodes
  const routeNodes = styleRouteNodes(trailPlanner, messages, hasAdminFeatures);
  const routeLayerExists = map.getLayer(routeNodesId);
  addMarkers(map, routeNodesId, routeNodes, [START, END, NODE, PLUS], null, null, true, hasAdminFeatures);
  if (!routeLayerExists) {
    addNodeEvents(map, trailPlanner);
  }
};

const removeTrailPlannerMap = map => {
  removeMarkers(map, routeNodesId, true);
  removeChevrons(map, routeChevronsId);
  removePolylines(map, routePolylinesId);
};

const removeInsertModeMap = map => {
  removeChevrons(map, routeInsertModeChevronsId)
  removePolylines(map, routeInsertModePolylinesId);
}

export { addTrailPlannerOverlay, removeTrailPlannerMap, removeInsertModeMap, routeNodesId, routeNodesDragId, newRouteNodeId, routePolylinesId, routeChevronsId };
